/**
 * Sales order store (S2.2)
 *
 * Zustand store for the SO list surface — filter state (persisted),
 * pagination echo, and the store-first list echo the mutation hooks update.
 *
 * Filter contract (holistic plan §S2.2 — GenericFilterPopover status /
 * customer / date):
 * - `search` — soNumber / externalOrderNumber (import dedupe key) / customer
 * - `status` — single-choice status chain filter ('all' = no filter)
 * - `priceStatus` — 價格審核 deviation-hold filter ('all' = no filter)
 * - `customerIds` — multi-select; >1 unions client-side over a mock-scale
 *   fetch (the mock list endpoint accepts a single customerId only)
 * - `dateRange` — orderDate recency buckets, filtered client-side
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { SalesOrder, SalesOrderPriceStatus, SalesOrderStatus } from '@/types/api/salesOrder';

export type SoStatusFilter = 'all' | SalesOrderStatus;
export type SoPriceStatusFilter = 'all' | SalesOrderPriceStatus;
export type SoDateRangeFilter = 'all' | '7d' | '30d' | '90d';

export interface SoFilters {
  search: string;
  status: SoStatusFilter;
  priceStatus: SoPriceStatusFilter;
  customerIds: number[];
  dateRange: SoDateRangeFilter;
}

export interface SoPagination {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface SoState {
  // Data (list echo — kept in sync by useSalesOrdersTable / mutation hooks)
  salesOrders: SalesOrder[];

  // Pagination echo
  pagination: SoPagination;

  // Filters
  filters: SoFilters;

  // Actions - data
  setSalesOrders: (salesOrders: SalesOrder[]) => void;
  upsertSalesOrderInList: (salesOrder: SalesOrder) => void;

  // Actions - pagination
  setPagination: (pagination: Partial<SoPagination>) => void;

  // Actions - filters
  setSearch: (search: string) => void;
  setStatus: (status: SoStatusFilter) => void;
  setPriceStatus: (priceStatus: SoPriceStatusFilter) => void;
  setCustomerIds: (customerIds: number[]) => void;
  setDateRange: (dateRange: SoDateRangeFilter) => void;
  resetFilters: () => void;

  // Actions - reset
  reset: () => void;
}

const initialState = {
  salesOrders: [] as SalesOrder[],
  pagination: {
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  },
  filters: {
    search: '',
    status: 'all' as SoStatusFilter,
    priceStatus: 'all' as SoPriceStatusFilter,
    customerIds: [] as number[],
    dateRange: 'all' as SoDateRangeFilter,
  },
};

export const useSoStore = create<SoState>()(
  devtools(
    persist(
      (set) => ({
        ...initialState,

        setSalesOrders: (salesOrders) => set({ salesOrders }, false, 'so/setSalesOrders'),

        upsertSalesOrderInList: (salesOrder) =>
          set(
            (state) => {
              const exists = state.salesOrders.some((so) => so.id === salesOrder.id);
              return {
                salesOrders: exists
                  ? state.salesOrders.map((so) => (so.id === salesOrder.id ? salesOrder : so))
                  : [salesOrder, ...state.salesOrders],
              };
            },
            false,
            'so/upsertSalesOrderInList'
          ),

        setPagination: (pagination) =>
          set(
            (state) => ({ pagination: { ...state.pagination, ...pagination } }),
            false,
            'so/setPagination'
          ),

        setSearch: (search) =>
          set(
            (state) => ({
              filters: { ...state.filters, search },
              pagination: { ...state.pagination, page: 1 },
            }),
            false,
            'so/setSearch'
          ),

        setStatus: (status) =>
          set(
            (state) => ({
              filters: { ...state.filters, status },
              pagination: { ...state.pagination, page: 1 },
            }),
            false,
            'so/setStatus'
          ),

        setPriceStatus: (priceStatus) =>
          set(
            (state) => ({
              filters: { ...state.filters, priceStatus },
              pagination: { ...state.pagination, page: 1 },
            }),
            false,
            'so/setPriceStatus'
          ),

        setCustomerIds: (customerIds) =>
          set(
            (state) => ({
              filters: { ...state.filters, customerIds },
              pagination: { ...state.pagination, page: 1 },
            }),
            false,
            'so/setCustomerIds'
          ),

        setDateRange: (dateRange) =>
          set(
            (state) => ({
              filters: { ...state.filters, dateRange },
              pagination: { ...state.pagination, page: 1 },
            }),
            false,
            'so/setDateRange'
          ),

        resetFilters: () =>
          set(
            {
              filters: { ...initialState.filters },
              pagination: { ...initialState.pagination },
            },
            false,
            'so/resetFilters'
          ),

        reset: () => set(initialState, false, 'so/reset'),
      }),
      {
        name: 'so-storage',
        partialize: (state) => ({
          filters: state.filters,
          pagination: {
            page: state.pagination.page,
            pageSize: state.pagination.pageSize,
          },
        }),
      }
    ),
    { name: 'SoStore' }
  )
);
